import { Box, styled } from '@mui/material';
import MuiTooltip, {
  tooltipClasses,
  TooltipProps as MuiTooltipProps,
} from '@mui/material/Tooltip';

import { COLORS } from '@/styles/tokens/colors';

const SCREEN_MARGIN = 16; // theme.spacing(2), consistent with Popper.

export interface TooltipProps extends MuiTooltipProps {
  /**
   * Whether to show the tooltip.
   *
   * @default true
   */
  show?: boolean;
}

export const Tooltip = styled(
  ({
    className,
    children,
    placement = 'top',
    show = true,
    title,
    classes,
    slotProps,
    ...props
  }: TooltipProps) => {
    return (
      <MuiTooltip
        // Allow tooltip to be focusable to make it accessible by keyboard
        tabIndex={0}
        arrow={true}
        classes={{ popper: className, ...classes }}
        placement={placement}
        // this is a little bit of a hack but it lets us explicitly hide the tooltip without any kind of
        // conditional rendering in the parent component
        // this works because if the length of the title is 0, the tooltip will not be shown
        title={show ? title : ''}
        {...props}
        slotProps={{
          ...slotProps,
          popper: (ownerState) => {
            const popperProps =
              typeof slotProps?.popper === 'function'
                ? slotProps.popper(ownerState)
                : (slotProps?.popper ?? props.componentsProps?.popper);

            return {
              ...popperProps,
              modifiers: [
                {
                  name: 'preventOverflow',
                  // Keep the inset even when the trigger is at the viewport edge.
                  options: {
                    padding: SCREEN_MARGIN,
                    altAxis: true,
                    tether: false,
                  },
                },
                { name: 'flip', options: { padding: SCREEN_MARGIN } },
                ...(ownerState.PopperProps?.modifiers ?? []),
                ...(popperProps?.modifiers ?? []),
              ],
            };
          },
        }}
      >
        <Box component="span">{children}</Box>
      </MuiTooltip>
    );
  }
)(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    '&:before': {
      border: `4px solid white`,
    },
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: COLORS.TEXT.SECONDARY,
    boxShadow: `${theme.palette.shadows.lg}, ${theme.palette.shadows.lgYInvert}`,
    padding: theme.spacing(1.5),
    fontSize: 12,
    lineHeight: '1rem',
    boxSizing: 'border-box',
    maxWidth: `min(300px, calc(100vw - ${SCREEN_MARGIN * 2}px))`,
    overflowWrap: 'anywhere',
  },
}));
